import { useMemo } from 'react';
import { useNavigationApi, resolveNodeRoute, resolveIcon, type NavigationNode } from './useNavigationApi';
import { useNavigation, type NotificationItem } from '@/contexts/NavigationContext';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { type LucideIcon } from 'lucide-react';

export interface ServiceMenuItem {
  visible: boolean;
  name: string;
  icon: LucideIcon;
}

export interface ServiceMenuSubItem {
  title: string;
  url: string;
}

export interface ServiceMenuResult {
  addVin: ServiceMenuItem;
  notifications: ServiceMenuItem;
  cart: ServiceMenuItem;
  userMenu: ServiceMenuItem;
  dealerMenu: ServiceMenuItem;
  userMenuItems: ServiceMenuSubItem[];
  dealerMenuItems: ServiceMenuSubItem[];
  notificationItems: NavigationNode[];
  notificationTotal: number;
  apiNotifications: NotificationItem[];
  isLoading: boolean;
  useMock: boolean;
}

/** Maps API navigationTag to internal keys (stable across CMS renames) */

/** Maps API navigationTag to internal keys (stable across CMS renames) */
const serviceMenuTagMap: Record<string, keyof Pick<ServiceMenuResult, 'addVin' | 'notifications' | 'cart' | 'userMenu' | 'dealerMenu'>> = {
  'AddVin': 'addVin',
  'Notifications': 'notifications',
  'ShoppingCart': 'cart',
  'MyAccount': 'userMenu',
  'MyDealership': 'dealerMenu',
};

const defaultNames: Record<string, string> = {
  addVin: 'Add VIN',
  notifications: 'Notifications',
  cart: 'Shopping Cart',
  userMenu: 'My Account',
  dealerMenu: 'My Dealership',
};

const defaultTags: Record<string, string> = {
  addVin: 'AddVin',
  notifications: 'Notifications',
  cart: 'Cart',
  userMenu: 'MyAccount',
  dealerMenu: 'Dealership',
};

function makeDefault(key: string): ServiceMenuItem {
  return { visible: true, name: defaultNames[key], icon: resolveIcon(defaultTags[key]) };
}

export function useServiceMenuNavigation(): ServiceMenuResult {
  const { nodes, isLoading, useMock } = useNavigationApi({
    rootNavigationTag: 'ServiceMenu',
    stopLevel: 3,
  });

  const { notifications: ctxNotifications, notificationTotal: ctxNotificationTotal } = useNavigation();
  const { isFeatureEnabled } = useFeatureFlags();
  const renderMockMappings = isFeatureEnabled('renderMockMappings');

  return useMemo(() => {
    // Mock mode: return all visible with defaults
    if (useMock) {
      return {
        addVin: makeDefault('addVin'),
        notifications: makeDefault('notifications'),
        cart: makeDefault('cart'),
        userMenu: makeDefault('userMenu'),
        dealerMenu: makeDefault('dealerMenu'),
        userMenuItems: [
          { title: 'Saved Carts', url: '/saved-carts' },
          { title: 'My Favorites', url: '/my-favorites' },
          { title: 'My Contact Info', url: '/contact-info' },
          
        ],
        dealerMenuItems: [
          { title: 'Dashboard', url: '/' },
          { title: 'Dealer Orders', url: '/orders' },
          { title: 'Dealer Invoices', url: '/dealer-invoices' },
          { title: 'Dealer Addresses', url: '/addresses' },
          { title: 'Units on Lot', url: '/units?filter=inventory' },
          
          { title: 'Manage Dealer Users', url: '/dealer-users' },
        ],
        notificationItems: [],
        notificationTotal: 11,
        apiNotifications: [
          { name: 'RGAs Pending Parts Return', amount: 6 },
          { name: 'Claim Requires Feedback', amount: 3 },
          { name: 'New Service Campaigns', amount: 2 },
        ],
        isLoading: false,
        useMock: true,
      };
    }

    // API mode: build from nodes
    const result: Record<string, ServiceMenuItem> = {
      addVin: { visible: false, name: defaultNames.addVin, icon: resolveIcon(defaultTags.addVin) },
      notifications: { visible: false, name: defaultNames.notifications, icon: resolveIcon(defaultTags.notifications) },
      cart: { visible: false, name: defaultNames.cart, icon: resolveIcon(defaultTags.cart) },
      userMenu: { visible: false, name: defaultNames.userMenu, icon: resolveIcon(defaultTags.userMenu) },
      dealerMenu: { visible: false, name: defaultNames.dealerMenu, icon: resolveIcon(defaultTags.dealerMenu) },
    };

    let userMenuItems: ServiceMenuSubItem[] = [];
    let dealerMenuItems: ServiceMenuSubItem[] = [];
    let notificationItems: NavigationNode[] = [];

    if (nodes) {
      for (const node of nodes) {
        if (!node.showInMenu) continue;
        const key = node.navigationTag ? serviceMenuTagMap[node.navigationTag] : undefined;
        if (!key) continue;

        result[key] = { visible: true, name: node.name, icon: node.navigationTag ? resolveIcon(node.navigationTag) : resolveIcon(defaultTags[key as keyof typeof defaultTags]) };

        // Extract sub-items for menus with children
        if (key === 'userMenu' && node.nodes) {
          userMenuItems = node.nodes
            .filter(n => n.showInMenu)
            .map(n => ({
              title: n.name,
              url: resolveNodeRoute(n),
            }));
        } else if (key === 'dealerMenu' && node.nodes) {
          dealerMenuItems = node.nodes
            .filter(n => n.showInMenu)
            .map(n => ({
              title: n.name,
              url: resolveNodeRoute(n),
            }));
        } else if (key === 'notifications' && node.nodes) {
          notificationItems = node.nodes.filter(n => n.showInMenu);
        }
      }
    }

    return {
      addVin: result.addVin,
      notifications: result.notifications,
      cart: result.cart,
      userMenu: result.userMenu,
      dealerMenu: result.dealerMenu,
      userMenuItems,
      dealerMenuItems,
      notificationItems,
      notificationTotal: ctxNotificationTotal,
      apiNotifications: ctxNotifications ?? [],
      isLoading,
      useMock: false,
    };
  }, [nodes, isLoading, useMock, renderMockMappings, ctxNotifications, ctxNotificationTotal]);
}
